import { Controller } from "@hotwired/stimulus"
import { ApiClient } from "../utils/api"

export default class extends Controller {
  static targets = [
    "userList", "userSearch", "messagesContainer",
    "userName", "messageInput", "messageForm",
    "sendButton", "emptyState", "chatHeader", "userStatus"
  ]

  connect() {
    this.selectedUserId = null
    // メッセージ入力欄のEnterは改行。送信は「送信」ボタンのクリックのみで行う。
  }

  filterUsers(event) {
    const searchTerm = event.target.value.toLowerCase()
    const userItems = this.userListTarget.querySelectorAll('.list-group-item')

    userItems.forEach(item => {
      const userName = item.querySelector('h6').textContent.toLowerCase()
      if (userName.includes(searchTerm)) {
        item.style.display = ''
      } else {
        item.style.display = 'none'
      }
    })
  }

  async selectUser(event) {
    const userItem = event.currentTarget
    this.selectedUserId = userItem.dataset.userId
    const userName = userItem.dataset.userName
    const isBlocked = userItem.dataset.lineBlocked === 'true'

    // UIを更新
    this.userNameTarget.textContent = userName

    // ブロック状態の表示
    if (this.hasUserStatusTarget) {
      if (isBlocked) {
        this.userStatusTarget.innerHTML = `
          <span class="badge bg-warning text-dark">
            <i class="bi bi-slash-circle me-1"></i>
            LINEブロック中
          </span>
        `
        // ブロック中は送信を無効化
        this.messageInputTarget.disabled = true
        this.sendButtonTarget.disabled = true
        this.messageInputTarget.placeholder = 'ユーザーがLINEをブロックしているため送信できません'
      } else {
        this.userStatusTarget.innerHTML = `
          <span class="badge bg-success">
            <i class="bi bi-check-circle me-1"></i>
            アクティブ
          </span>
        `
        this.messageInputTarget.disabled = false
        this.sendButtonTarget.disabled = false
        this.messageInputTarget.placeholder = 'メッセージを入力...'
      }
    } else {
      // userStatusTargetがない場合の後方互換性
      this.messageInputTarget.disabled = isBlocked
      this.sendButtonTarget.disabled = isBlocked
    }

    // emptyStateが存在する場合のみ非表示にする
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.style.display = 'none'
    }

    // 選択状態を更新
    this.userListTarget.querySelectorAll('.list-group-item').forEach(item => {
      item.classList.remove('active')
    })
    userItem.classList.add('active')

    // メッセージを取得
    await this.loadMessages()
  }

  async loadMessages() {
    try {
      const response = await ApiClient.get(`/admin/messages/${this.selectedUserId}/chat_messages`)
      const data = await response.json()

      this.displayMessages(data.messages)
      this.scrollToBottom()

      // 未読バッジを更新（0にリセット）
      this.updateUnreadBadge(this.selectedUserId, 0)
    } catch (error) {
      console.error('メッセージの読み込みに失敗しました:', error)
    }
  }

  displayMessages(messages) {
    // emptyStateを非表示にしてからメッセージを表示
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.style.display = 'none'
    }

    this.messagesContainerTarget.innerHTML = ''

    messages.forEach(message => {
      const messageElement = this.createMessageElement(message)
      this.messagesContainerTarget.appendChild(messageElement)
    })
  }

  createMessageElement(message) {
    const div = document.createElement('div')

    // システムイベントの場合は特別な表示
    if (message.message_type === 'system_event') {
      div.className = 'd-flex justify-content-center mb-3'

      const eventLabel = document.createElement('div')
      eventLabel.className = 'badge bg-light text-dark border px-3 py-2'

      let icon = ''
      let text = ''

      switch (message.content) {
        case 'line_blocked':
          icon = '<i class="bi bi-slash-circle text-warning me-2"></i>'
          text = 'LINEブロック'
          break
        case 'line_unblocked':
          icon = '<i class="bi bi-check-circle text-success me-2"></i>'
          text = 'LINEブロック解除'
          break
        default:
          icon = '<i class="bi bi-info-circle text-info me-2"></i>'
          text = message.content
      }

      eventLabel.innerHTML = `
        ${icon}
        <span>${text}</span>
        <small class="text-muted ms-2">${message.created_at}</small>
      `

      div.appendChild(eventLabel)
    } else {
      // 通常のメッセージ
      div.className = `d-flex mb-3 ${message.is_outbound ? 'justify-content-end' : 'justify-content-start'}`

      const messageBody = document.createElement('div')
      messageBody.className = `rounded p-3 ${message.is_outbound ? 'bg-primary text-white' : 'bg-white border'}`
      messageBody.style.maxWidth = '70%'

      // 一括送信メッセージの場合はバッジを追加
      let bulkBadge = ''
      if (message.bulk_message_id) {
        bulkBadge = `
          <span class="badge bg-warning text-dark mb-2">
            <i class="bi bi-megaphone me-1"></i>
            一括送信
          </span>
          <br>
        `
      }

      messageBody.innerHTML = `
        ${bulkBadge}
        <div class="mb-1 message-text">${this.escapeHtml(message.content)}</div>
        <small class="${message.is_outbound ? 'text-white-50' : 'text-muted'}">${message.created_at}</small>
      `

      div.appendChild(messageBody)
    }

    return div
  }

  async sendMessage(event) {
    event.preventDefault()

    const content = this.messageInputTarget.value.trim()
    if (!content || !this.selectedUserId) return

    // 送信前に確認ダイアログを表示（誤送信防止）
    if (!window.confirm('このメッセージを送信しますか？')) return

    // 送信中の状態を設定
    this.setLoadingState(true)
    const originalContent = this.messageInputTarget.value
    this.messageInputTarget.value = ''

    try {
      const response = await ApiClient.post(
        `/admin/messages/${this.selectedUserId}/send_chat_message`,
        { content }
      )

      const data = await response.json()

      if (data.success) {
        // メッセージを追加
        const messageElement = this.createMessageElement(data.message)
        this.messagesContainerTarget.appendChild(messageElement)

        // 最下部にスクロール
        this.scrollToBottom()

        // ユーザーリストの最終メッセージを更新
        this.updateUserListLastMessage(this.selectedUserId, content)
      } else {
        // エラー時は入力内容を復元
        this.messageInputTarget.value = originalContent
        if (window.showToast) {
          window.showToast('メッセージの送信に失敗しました', 'error')
        } else {
          console.error('メッセージの送信に失敗しました')
        }
      }
    } catch (error) {
      // エラー時は入力内容を復元
      this.messageInputTarget.value = originalContent
      console.error('メッセージの送信に失敗しました:', error)
      if (window.showToast) {
        window.showToast('通信エラーが発生しました', 'error')
      } else {
        console.error('通信エラーが発生しました')
      }
    } finally {
      // 送信状態を解除
      this.setLoadingState(false)
    }
  }

  setLoadingState(loading) {
    this.messageInputTarget.disabled = loading
    this.sendButtonTarget.disabled = loading

    if (loading) {
      this.sendButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>送信中...'
    } else {
      this.sendButtonTarget.innerHTML = '<i class="bi bi-send me-1"></i>送信'
    }
  }

  updateUserListLastMessage(userId, message) {
    const userItem = this.userListTarget.querySelector(`[data-user-id="${userId}"]`)
    if (!userItem) return

    const lastMessageElement = userItem.querySelector('.text-muted.small')
    if (lastMessageElement) {
      lastMessageElement.textContent = message.length > 30 ? message.substring(0, 30) + '...' : message
    }
  }

  scrollToBottom() {
    this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  updateUnreadBadge(userId, count) {
    const userItem = this.userListTarget.querySelector(`[data-user-id="${userId}"]`)
    if (!userItem) return

    const badge = userItem.querySelector('.badge')
    if (count === 0) {
      // バッジを削除
      if (badge) badge.remove()
    } else {
      // バッジを更新または作成
      if (badge) {
        badge.textContent = count
      } else {
        const newBadge = document.createElement('span')
        newBadge.className = 'badge bg-danger rounded-pill'
        newBadge.textContent = count
        userItem.querySelector('.d-flex').appendChild(newBadge)
      }
    }
  }
}
